import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar as CalendarIcon, CarFront, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import api from "../lib/api";

const TYPES = [
  { value: "", label: "Any type" },
  { value: "compact", label: "Compact" },
  { value: "sedan", label: "Sedan" },
  { value: "suv", label: "SUV" },
  { value: "bakkie", label: "Bakkie" },
  { value: "hatchback", label: "Hatchback" },
  { value: "convertible", label: "Convertible" },
  { value: "luxury", label: "Luxury" },
];

function Field({ icon, label, value, placeholder, trailing, testId }) {
  return (
    <button
      type="button"
      className="w-full flex items-start gap-3.5 text-left px-5 py-4 rounded-2xl hover:bg-[#F4F8FF] transition-colors"
      data-testid={testId}
    >
      <span className="mt-0.5 w-8 h-8 flex-shrink-0 rounded-full bg-[#EAF2FF] text-[#1d7ffd] flex items-center justify-center">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-bold text-[#0a1f4d] leading-tight">{label}</span>
        <span className={`mt-1 flex items-center justify-between gap-2 text-[15px] leading-tight truncate ${value ? "text-[#0a1f4d] font-semibold" : "text-[#64748B]"}`}>
          <span className="truncate">{value || placeholder}</span>
          {trailing}
        </span>
      </span>
    </button>
  );
}

export default function SearchBar({ initial = {} }) {
  const navigate = useNavigate();
  const [city, setCity] = useState(initial.city || "");
  const [vehicleType, setVehicleType] = useState(initial.vehicle_type || "");
  const [from, setFrom] = useState(initial.range?.from);
  const [to, setTo] = useState(initial.range?.to);
  const [cities, setCities] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);

  useEffect(() => {
    api.get("/vehicles/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  const submit = () => {
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (vehicleType) p.set("vehicle_type", vehicleType);
    if (from) p.set("from", from.toISOString());
    if (to) p.set("to", to.toISOString());
    navigate(`/vehicles?${p.toString()}`);
  };

  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const optionCls = "w-full text-left px-3 py-2 rounded-md text-sm hover:bg-[#F4F8FF] text-[#0a1f4d]";

  return (
    <div
      className="bg-white rounded-[2rem] search-card-shadow border border-[#EEF2F7] p-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.25fr_1fr_1fr_1fr_auto] items-center lg:divide-x divide-[#E7ECF3]"
      data-testid="search-bar"
    >
      <Popover open={cityOpen} onOpenChange={setCityOpen}>
        <PopoverTrigger asChild>
          <div>
            <Field icon={<MapPin size={16} />} label="Pick-up location" value={city} placeholder="City, airport or address" testId="search-city-btn" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <button className={optionCls} onClick={() => { setCity(""); setCityOpen(false); }} data-testid="search-city-anywhere">
            Anywhere in SA
          </button>
          {cities.map((c) => (
            <button key={c} onClick={() => { setCity(c); setCityOpen(false); }} className={optionCls} data-testid={`search-city-${c}`}>
              {c}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <Popover open={fromOpen} onOpenChange={setFromOpen}>
        <PopoverTrigger asChild>
          <div>
            <Field icon={<CalendarIcon size={16} />} label="Pick-up date" value={from ? format(from, "EEE, d MMM") : ""} placeholder="Select date" testId="search-dates-btn" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start">
          <Calendar
            mode="single"
            selected={from}
            onSelect={(d) => { setFrom(d); if (to && d && to < d) setTo(undefined); setFromOpen(false); if (d) setToOpen(true); }}
            disabled={(d) => d < today}
          />
        </PopoverContent>
      </Popover>

      <Popover open={toOpen} onOpenChange={setToOpen}>
        <PopoverTrigger asChild>
          <div>
            <Field icon={<CalendarIcon size={16} />} label="Drop-off date" value={to ? format(to, "EEE, d MMM") : ""} placeholder="Select date" testId="search-dropoff-btn" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0" align="start">
          <Calendar
            mode="single"
            selected={to}
            defaultMonth={from}
            onSelect={(d) => { setTo(d); setToOpen(false); }}
            disabled={(d) => d < (from || today)}
          />
        </PopoverContent>
      </Popover>

      <Popover open={typeOpen} onOpenChange={setTypeOpen}>
        <PopoverTrigger asChild>
          <div>
            <Field
              icon={<CarFront size={16} />}
              label="Car type"
              value={vehicleType ? TYPES.find((t) => t.value === vehicleType)?.label : ""}
              placeholder="Any type"
              trailing={<ChevronDown size={16} className="flex-shrink-0 text-[#0a1f4d]" />}
              testId="search-type-btn"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          {TYPES.map((t) => (
            <button key={t.value || "any"} onClick={() => { setVehicleType(t.value); setTypeOpen(false); }} className={optionCls} data-testid={`search-type-${t.value || "any"}`}>
              {t.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>

      <div className="p-2 sm:col-span-2 lg:col-span-1 lg:pl-4">
        <button onClick={submit} className="btn-primary w-full lg:w-auto !px-8 !py-4 text-base" data-testid="search-submit-button">
          <Search size={18} />
          <span>Search</span>
        </button>
      </div>
    </div>
  );
}
